'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import {
  CategoryParamsSync,
  CategoryProvider,
  useCategoryStore,
} from '@eliadmin/core/src/stores/category-store';
import { parseCategoryFilterState } from '@/lib/category-filter-state';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  Suspense,
  type PropsWithChildren,
} from 'react';

type FilterContextState = {
  status: 'loading' | 'ready' | 'error';
  load: () => void;
  retry: () => void;
};

const FilterContext = createContext<FilterContextState>({
  status: 'loading',
  load: () => undefined,
  retry: () => undefined,
});

export function useCategoryFilterContext() {
  return useContext(FilterContext);
}

function CategoryMetadataSync({ category }: { category: Category & { slug: string } }) {
  const setCategory = useCategoryStore(state => state.setCategory);
  useEffect(() => setCategory(category), [category, setCategory]);
  return null;
}

export function LazyCategoryProvider({
  children,
  category,
  categoryTree = [],
  locale,
  slug,
  initialSearch,
}: PropsWithChildren<{
  category: Category;
  categoryTree?: CategoriesEntity[];
  locale: string;
  slug: string;
  initialSearch?: string;
}>) {
  const activeCategory = useMemo(
    () => ({
      ...category,
      // The supported slug API already returns filters. Reuse that response;
      // do not discard its fields and refetch the same category after mount.
      attributes: category.attributes ?? [],
      tags: category.tags ?? [],
      manufacturers: category.manufacturers ?? [],
      availabilities: category.availabilities ?? [],
      suppliers: category.suppliers ?? [],
      supplier_locations: category.supplier_locations ?? [],
      statuses: category.statuses ?? [],
      variants: category.variants ?? [],
      price: category.price ?? { min: null, max: null },
      slug,
    }),
    [category, slug],
  );

  const contextValue = useMemo<FilterContextState>(
    () => ({
      status: 'ready',
      load: () => undefined,
      retry: () => undefined,
    }),
    [],
  );

  return (
    <FilterContext.Provider value={contextValue}>
      <CategoryProvider
        key={`category-${locale}-${category.id}-${slug}`}
        state={() => ({
          category: activeCategory,
          categoryTree,
          ...(initialSearch !== undefined
            ? {
                ...parseCategoryFilterState(new URLSearchParams(initialSearch)),
                paramsReady: true,
              }
            : {}),
        })}
      >
        <CategoryMetadataSync category={activeCategory} />
        <Suspense>
          <CategoryParamsSync />
        </Suspense>
        {children}
      </CategoryProvider>
    </FilterContext.Provider>
  );
}
